import { isAxiosError } from "axios";
import { LogCategory } from "Enumerations";
import { useCallback } from "react";
import { ToastNotificationModel } from "Shared/Components/Toast";
import { isServiceErrorDetails, newGuid } from "Shared/Utils";
import { ZodError } from "zod";

import { Mutation, Query } from "@tanstack/react-query";

import { useToast } from "../../Shared/Hooks/Core/useToast";
import { useLogging } from "../../Shared/Hooks/Logging/useLogging";
import { ZodiosError } from "@zodios/core";
import { ORPCError, ValidationError } from "@orpc/contract";

export const useQueryErrorHandler = () => {
    const { showToastNotification } = useToast();
    const { trackException } = useLogging(LogCategory.Api);

    const queryErrorHandler = useCallback(
        (error: unknown, queries: Query<unknown, unknown>) => {
            if (error instanceof ORPCError || error instanceof ValidationError) {
                return;
            }
            let correlation = newGuid();
            if (error instanceof ZodiosError) {
                correlation = getCorrelationId(error);
                error = error.cause;
            }
            if (isAxiosError(error) || isServiceErrorDetails(error)) {
                return;
            }
            let message = "Query API response unknown error";
            if (error instanceof ZodError) {
                message = "Query API response verification error";
            }
            trackException("6857e40f-8842-496f-a14f-1d6123a3a3a8", error, message, {
                Query: queries.queryHash,
                CorrelationID: correlation
            });
            if (queries.meta?.suppressGlobalErrorToast === true) {
                return;
            }

            const toastModel: ToastNotificationModel = {
                title: message,
                toastType: "error",
                duration: "Long",
                aggregateType: "ZodError"
            };
            showToastNotification(toastModel);
        },
        [showToastNotification, trackException]
    );

    const mutationErrorHandler = useCallback(
        (error: unknown, variables: unknown, context: unknown, mutation: Mutation<unknown, unknown, unknown, unknown>) => {
            if (error instanceof ORPCError || error instanceof ValidationError) {
                return;
            }
            let correlation = newGuid();
            if (error instanceof ZodiosError) {
                correlation = getCorrelationId(error);
                error = error.cause;
            }
            if (isAxiosError(error) || isServiceErrorDetails(error)) {
                return;
            }
            let message = "Mutation API response unknown error";
            if (error instanceof ZodError) {
                message = "Mutation API response verification error";
            }

            trackException("9ebd9f7b-3161-4dc3-923e-a53336019eb9", error, message, { CorrelationID: correlation });

            const toastModel: ToastNotificationModel = {
                title: message,
                toastType: "error",
                duration: "Long",
                aggregateType: "ZodError"
            };
            showToastNotification(toastModel);
        },
        [showToastNotification, trackException]
    );

    return { queryErrorHandler, mutationErrorHandler };
};

function getCorrelationId(error: ZodiosError) {
    const headers = error.config?.headers;
    return headers?.["x-correlation"] ?? newGuid();
}
